import React from 'react';
import styled, { css } from 'styled-components';
import { remCalc } from '@prisjakt/ui-base';
import Button from '@prisjakt/ui-button';
import HideableBox, { DELAYS } from '@prisjakt/ui-hideable-box';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import CrossIcon from 'mdi-react/CloseIcon';
import { layout } from '@prisjakt/ui-base/styles/variables';
import CategoryLink from './category-link';

const sidebarWidth = 275;

const textLinkStyle = css`
  cursor: pointer;
  transition: color 0.1s linear;

  &:hover,
  &.focus-visible {
    color: ${props => props.theme.text.callToAction};
  }

  &.focus-visible {
    text-decoration: underline;
  }

  &:focus:focus {
    outline: 0;
  }
`;

const CategoryTextLink = styled(CategoryLink)`
  ${textLinkStyle}
`;

export const Wrapper = styled.nav`
  display: flex;
  height: 75vh;
  margin-left: auto;
  margin-right: auto;
  max-width: 1250px;
  position: relative;
`;

export const TopLevelMenu = styled.ul`
  flex: 0 0 ${sidebarWidth}px;
  list-style: none;
  margin: 0;
  max-height: 100%;
  overflow: auto;
  padding: ${layout.spacing.medium} ${layout.spacing.small};
`;

export const TopLevelItem = styled.li``;

type TopLevelButtonProps = {
  isOpen?: boolean;
};
export const TopLevelButton = styled.button.attrs({ type: 'button' })<
  TopLevelButtonProps
>`
  align-items: center;
  background: ${({ isOpen, theme }) =>
    isOpen ? theme.buttons.category.bgActive : theme.buttons.category.bg};
  border-radius: ${layout.borderRadius};
  /* We use a box shadow on hover as a border that doesn't affect the layout.
     Setting a transparent box shadow here lets us transition only the color later. */
  box-shadow: inset 0 0 0 1px transparent;
  color: ${({ theme }) => theme.buttons.category.text};
  cursor: pointer;
  display: flex;
  font-weight: 600;
  padding: ${layout.spacing.xSmall};
  position: relative;
  transition-property: background-color, box-shadow, color;
  transition-duration: 0.15s;
  transition-timing-function: linear;
  width: 100%;

  /* Double selector to override e.g. Firefox's default focus styling */
  &:focus:focus {
    outline: 0;
  }

  ${({ isOpen, theme }) =>
    !isOpen &&
    css`
      &:hover,
      &.focus-visible {
        box-shadow: inset 0 0 0 1px ${theme.lines.primary};
        color: ${theme.buttons.category.textHover};
      }
    `};
`;

export const TopLevelIcon = styled.img`
  flex: 0 0 40px;
  height: 40px;
  margin-right: ${layout.spacing.xSmall};
`;

const categoryNameHeight = 24;

export const TopLevelCategoryName = styled.div`
  line-height: ${remCalc(categoryNameHeight)};
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TopLevelArrow = styled(ChevronRightIcon).attrs({
  size: categoryNameHeight,
})`
  margin-left: auto;
`;

function boxShadowBorder(
  color: string,
  spacing: number = 0,
  width: number = 1
): string {
  return `${remCalc(-spacing - width)} 0 0 ${remCalc(-spacing)} ${color}`;
}

export const SubMenuArea = styled.div`
  /* Use a box-shadow instead of a border in order to create spacing above and below it */
  box-shadow: ${props =>
    boxShadowBorder(props.theme.lines.primary, layout.spacing.values.small, 1)};
  margin-left: 1px;
  display: flex;
  /* IE11 ignores calc values in the shorthand flex property, but setting it separately works */
  flex: 0 0 auto;
  flex-basis: calc(100% - ${sidebarWidth}px);
  max-height: 100%;
  overflow: auto;
  position: relative;
`;

type TopLevelHeadingProps = {
  showDivider: boolean;
};
export const TopLevelHeading = styled.h2<TopLevelHeadingProps>`
  background: ${props => props.theme.bg.primary};
  font-size: 20px;
  margin: 0;
  padding: ${layout.spacing.large};
  padding-bottom: ${remCalc(layout.spacing.values.small + 1)};
  position: sticky;
  top: 0;

  ::after {
    border-bottom: 1px solid
      ${props =>
        props.showDivider ? props.theme.lines.primary : 'transparent'};
    bottom: 0;
    content: '';
    display: block;
    position: absolute;
    transition: border-color 0.2s linear;
    width: calc(100% - ${remCalc(layout.spacing.values.large * 2)});
  }
`;

export const TopLevelLink = styled(CategoryTextLink)``;

export const CategoryView = styled(HideableBox).attrs({
  delayIn: DELAYS.SHORTER,
})`
  flex-direction: column;
  position: absolute;
  width: 100%;
`;

export const SecondLevelMenu = styled.div`
  display: flex;
  line-height: 1.75;
  list-style: none;
  padding: ${layout.spacing.large};
  padding-top: 0;
  width: 100%;
`;

export const SecondLevelColumn = styled.div`
  flex: 1 0 30%;
  padding-right: ${layout.spacing.small};
  &:last-child {
    padding-right: 0;
  }
`;

export const SecondLevelItem = styled.div`
  break-inside: avoid;
  margin: 0 0 ${layout.spacing.medium} 0;
`;

export const SecondLevelHeading = styled.h3`
  color: ${props => props.theme.text.primary};
  font: inherit;
  font-weight: 600;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SecondLevelLink = styled(CategoryTextLink)``;

export const ThirdLevelMenu = styled.ul`
  line-height: inherit;
  list-style: none;
  margin: 0;
`;

export const ThirdLevelItem = styled.li`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ThirdLevelLink = styled(CategoryTextLink)`
  color: ${props => props.theme.text.secondary};
`;

export const ShowAllButton = styled.button.attrs({ type: 'button' })`
  ${textLinkStyle}
  color: ${props => props.theme.text.tertiary};
  line-height: inherit;
`;

export const CloseMenuButton = styled(Button).attrs({
  color: 'secondary',
  variant: 'icon',
  icon: <CrossIcon />,
})`
  position: absolute;
  right: 30px;
  top: 30px;
`;
